/**
 * SermonClipperDrawer — records sermon audio (auto during sermon blocks
 * OR via manual start/stop) and uploads it to
 * `POST /admin/worship/sermons/{id}/clips`. The server Whisper-
 * transcribes it and asks the picked LLM for 3-5 shareable ~60-second
 * moments. This dialog lists past clips, renders their AI moments with a
 * click-to-seek audio player, and lets the operator copy the social
 * caption for each moment.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Mic, Square, Trash2, Copy, Scissors, X, Play,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

const MODELS = [
  { key: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { key: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { key: 'gpt-5.4-mini', label: 'GPT 5.4 Mini' },
];

export default function SermonClipperDrawer({
  serviceId, isSermonActive, onClose,
}) {
  const qc = useQueryClient();
  const [recording, setRecording] = useState(false);
  const [autoCaptured, setAutoCaptured] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [model, setModel] = useState(MODELS[0].key);
  const [uploading, setUploading] = useState(false);
  const [expandedClipId, setExpandedClipId] = useState(null);
  const [audioRefs] = useState({});

  const mrRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const startAtRef = useRef(0);
  const tickRef = useRef(null);
  const autoModeRef = useRef(false);

  const clipsQ = useQuery({
    queryKey: ['worship-clips', serviceId],
    queryFn: async () => (await axios.get(
      `${API}/api/admin/worship/sermons/${serviceId}/clips`, { headers: authH() },
    )).data,
  });

  const stopRecording = useCallback(async () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (!mrRef.current) return;
    return new Promise((resolve) => {
      mrRef.current.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        chunksRef.current = [];
        const durSec = (Date.now() - startAtRef.current) / 1000;
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
        mrRef.current = null;
        setRecording(false);
        setElapsedMs(0);
        // Only upload clips longer than ~5s
        if (blob.size < 10000 || durSec < 5) {
          toast.info('Recording too short — discarded');
          resolve(null);
          return;
        }
        setUploading(true);
        try {
          const form = new FormData();
          form.append('audio', blob, 'sermon.webm');
          const url = `${API}/api/admin/worship/sermons/${serviceId}/clips`
            + `?model=${encodeURIComponent(model)}`
            + `&auto_captured=${autoModeRef.current ? 'true' : 'false'}`
            + `&duration_sec=${durSec.toFixed(1)}`;
          await axios.post(url, form, {
            headers: { ...authH() },
            timeout: 120000,
          });
          toast.success('Clip processed — AI moments ready');
          qc.invalidateQueries({ queryKey: ['worship-clips', serviceId] });
        } catch (e) {
          toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
        } finally {
          setUploading(false);
          resolve(null);
        }
      };
      try { mrRef.current.stop(); } catch (e) { resolve(null); }
    });
  }, [model, qc, serviceId]);

  const startRecording = useCallback(async (isAuto = false) => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.start(1000);
      mrRef.current = mr;
      startAtRef.current = Date.now();
      autoModeRef.current = isAuto;
      setAutoCaptured(isAuto);
      setRecording(true);
      setElapsedMs(0);
      tickRef.current = setInterval(() => setElapsedMs(Date.now() - startAtRef.current), 500);
    } catch (e) {
      toast.error(`Mic access denied: ${e.message}`);
    }
  }, [recording]);

  // Auto-capture: when a sermon block becomes active, offer to start
  // (not aggressive — just a suggestion). If the operator has manual
  // control, this is a no-op.
  const [autoSuggested, setAutoSuggested] = useState(false);
  useEffect(() => {
    if (isSermonActive && !recording && !uploading && !autoSuggested) {
      setAutoSuggested(true);
      toast.custom((t) => (
        <div className="flex items-center gap-3 rounded-lg border border-fuchsia-500/40 bg-slate-900 px-3 py-2 text-sm text-slate-100 shadow-xl">
          <Mic className="h-4 w-4 text-fuchsia-300" />
          <div>
            <div className="font-semibold">Sermon block detected</div>
            <div className="text-xs text-slate-400">Auto-record for the Sermon Clipper?</div>
          </div>
          <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-500"
                  data-testid="clipper-auto-yes"
                  onClick={() => { toast.dismiss(t); startRecording(true); }}>
            Yes, record
          </Button>
          <button type="button" onClick={() => toast.dismiss(t)}
                  className="text-slate-500 hover:text-slate-200"><X className="h-3 w-3" /></button>
        </div>
      ), { duration: 12000 });
    }
    if (!isSermonActive) setAutoSuggested(false);
  }, [isSermonActive, recording, uploading, autoSuggested, startRecording]);

  const delMut = useMutation({
    mutationFn: async (id) => (await axios.delete(
      `${API}/api/admin/worship/sermons/${serviceId}/clips/${id}`,
      { headers: authH() },
    )).data,
    onSuccess: () => {
      toast.success('Clip deleted');
      qc.invalidateQueries({ queryKey: ['worship-clips', serviceId] });
    },
  });

  const secStr = (n) => {
    if (!n || n < 0) return '0:00';
    const m = Math.floor(n / 60);
    const s = Math.floor(n % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const seek = (clipId, seconds) => {
    const el = audioRefs[clipId];
    if (!el) return;
    try { el.currentTime = seconds; el.play(); } catch (e) { /* noop */ }
  };

  const copyCaption = (text) => {
    try { navigator.clipboard.writeText(text); toast.success('Caption copied'); }
    catch (e) { toast.error('Copy failed'); }
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 max-h-[75vh] overflow-y-auto border-t border-slate-800 bg-slate-950/95 p-3 shadow-2xl" data-testid="clipper-drawer">
      <div className="mx-auto max-w-5xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <div className="text-sm font-semibold text-slate-100 flex items-center gap-2">
              <Scissors className="h-4 w-4 text-fuchsia-300" /> Sermon Clipper
              {isSermonActive && <Badge variant="outline" className="border-emerald-500/40 text-emerald-200 text-[9px]">sermon active</Badge>}
              {autoCaptured && recording && <Badge variant="outline" className="border-fuchsia-500/40 text-fuchsia-200 text-[9px]">auto</Badge>}
            </div>
            <div className="text-[10px] text-slate-500">
              Records sermon audio, transcribes it with Whisper, then picks 3-5 shareable ~60 s moments.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Model</label>
            <select
              data-testid="clipper-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="h-8 rounded border border-slate-700 bg-slate-950 px-2 text-xs text-slate-200"
            >
              {MODELS.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
            </select>
            {!recording ? (
              <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-500"
                      onClick={() => startRecording(false)} disabled={uploading}
                      data-testid="clipper-start">
                <Mic className="mr-1 h-3 w-3" /> {uploading ? 'Processing…' : 'Start recording'}
              </Button>
            ) : (
              <Button size="sm" className="bg-rose-600 hover:bg-rose-500"
                      onClick={stopRecording}
                      data-testid="clipper-stop">
                <Square className="mr-1 h-3 w-3" /> Stop ({secStr(elapsedMs / 1000)})
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={onClose} data-testid="clipper-close">Close</Button>
          </div>
        </div>

        {/* Clips list */}
        <div className="space-y-2">
          {(clipsQ.data?.items || []).length === 0 && !clipsQ.isLoading && (
            <p className="text-center text-xs text-slate-500 py-4">No clips yet. Start a recording to create the first one.</p>
          )}
          {(clipsQ.data?.items || []).map((clip) => {
            const isOpen = expandedClipId === clip.id;
            return (
              <div key={clip.id} data-testid={`clip-${clip.id}`}
                   className="rounded-lg border border-slate-800 bg-slate-950/70 p-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-xs text-slate-300">
                    <span className="text-slate-500">{new Date(clip.created_at).toLocaleString()}</span>
                    {clip.duration_sec && <span className="ml-2">· {secStr(clip.duration_sec)}</span>}
                    <span className="ml-2 text-[10px] text-slate-500">via {clip.model}</span>
                    {clip.auto_captured && (
                      <Badge variant="outline" className="ml-2 border-fuchsia-500/40 text-fuchsia-200 text-[9px]">auto</Badge>
                    )}
                    <span className="ml-2 text-emerald-300">· {(clip.moments || []).length} moments</span>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => setExpandedClipId(isOpen ? null : clip.id)}
                            data-testid={`clip-toggle-${clip.id}`}
                            className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-slate-200 hover:border-slate-500">
                      {isOpen ? 'Hide' : 'Open'}
                    </button>
                    <button type="button" onClick={() => delMut.mutate(clip.id)}
                            data-testid={`clip-delete-${clip.id}`}
                            className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-rose-300 hover:border-rose-500">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-2 space-y-2">
                    <audio
                      ref={(el) => { if (el) audioRefs[clip.id] = el; }}
                      controls
                      src={`${API}${clip.audio_url}`}
                      className="w-full"
                      data-testid={`clip-audio-${clip.id}`}
                    />
                    {(clip.moments || []).length === 0 && (
                      <p className="text-xs text-slate-500">No AI moments were produced — try a different model.</p>
                    )}
                    {(clip.moments || []).map((m, i) => (
                      <div key={i} data-testid={`moment-${clip.id}-${i}`}
                           className="rounded border border-slate-800 bg-slate-900 p-2 text-xs">
                        <div className="flex items-center justify-between">
                          <div className="font-semibold text-slate-100">
                            <span className="text-emerald-300 mr-2">{m.title || `Moment ${i + 1}`}</span>
                            <span className="text-[10px] text-slate-500">
                              {secStr(m.start_sec)} → {secStr(m.end_sec)} · {Math.round(m.end_sec - m.start_sec)}s
                            </span>
                          </div>
                          <div className="flex gap-1">
                            <button type="button" onClick={() => seek(clip.id, m.start_sec)}
                                    data-testid={`moment-play-${clip.id}-${i}`}
                                    className="rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-200 hover:bg-emerald-500/20">
                              <Play className="mr-0.5 inline-block h-3 w-3" /> Play
                            </button>
                            <button type="button" onClick={() => copyCaption(m.caption_for_social || m.quote)}
                                    data-testid={`moment-copy-${clip.id}-${i}`}
                                    className="rounded border border-slate-700 bg-slate-900 px-2 py-0.5 text-[10px] text-slate-200 hover:border-slate-500">
                              <Copy className="mr-0.5 inline-block h-3 w-3" /> Copy
                            </button>
                          </div>
                        </div>
                        <p className="mt-1 italic text-slate-300">&ldquo;{m.quote}&rdquo;</p>
                        {m.caption_for_social && (
                          <p className="mt-1 text-[11px] text-fuchsia-200">{m.caption_for_social}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
